import React, { useEffect, useState } from "react";
import { getDepartments } from "../api/departmentService";
import { getProgramsByDepartment } from "../api/programService";
import { getSemestersByProgram } from "../api/semesterService";
import { getCoursesBySemester } from "../api/courseService";
import { getUnitsByCourse } from "../api/courseUnitService";
import {
  createLesson,
  getLessonsByUnit,
  getLessonById,
  updateLesson,
} from "../api/lessonService";

import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import "../styles/AddLesson.css";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/* ---------------- QUILL CONFIG ---------------- */
const quillModules = {
  toolbar: {
    container: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      ["blockquote", "code-block"],
      ["link", "image", "video"],
      ["clean"],
    ],
  },
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "align",
  "list",
  "bullet",
  "indent",
  "blockquote",
  "code-block",
  "link",
  "image",
  "video",
];

const AddLesson = () => {
  const [departments, setDepartments] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [courses, setCourses] = useState([]);
  const [units, setUnits] = useState([]);
  const [lessons, setLessons] = useState([]);

  /* ✅ SEARCH STATE (NOW USED) */
  const [search, setSearch] = useState("");

  const [editingLesson, setEditingLesson] = useState(null);
  const [saving, setSaving] = useState(false);

  /* 🔄 DROPDOWN LOADERS */
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [loadingSemesters, setLoadingSemesters] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);

  const [formData, setFormData] = useState({
    department_id: "",
    program_id: "",
    semester_id: "",
    course_id: "",
    unit_id: "",
    lesson_name: "",
    code: "",
    notes: "",
  });

  useEffect(() => {
    loadDepartments();
  }, []);

  /* ---------------- LOADERS ---------------- */

  const loadDepartments = async () => {
    try {
      const data = await getDepartments({ fields: "id,name" });
      setDepartments(data);
    } catch {
      toast.error("Failed to load departments");
    }
  };

  const loadPrograms = async (departmentId) => {
    setLoadingPrograms(true);
    setPrograms([]);
    setSemesters([]);
    setCourses([]);
    setUnits([]);
    setLessons([]);
    setEditingLesson(null);

    try {
      const data = await getProgramsByDepartment(departmentId);
      setPrograms(data);
    } catch {
      toast.error("Failed to load programs");
    } finally {
      setLoadingPrograms(false);
    }
  };

  const loadSemesters = async (programId) => {
    setLoadingSemesters(true);
    setSemesters([]);
    setCourses([]);
    setUnits([]);
    setLessons([]);
    setEditingLesson(null);

    try {
      const data = await getSemestersByProgram(programId);
      setSemesters(data);
    } catch {
      toast.error("Failed to load semesters");
    } finally {
      setLoadingSemesters(false);
    }
  };

  const loadCourses = async (semesterId) => {
    setLoadingCourses(true);
    setCourses([]);
    setUnits([]);
    setLessons([]);
    setEditingLesson(null);

    try {
      const data = await getCoursesBySemester(semesterId);
      setCourses(data);
    } catch {
      toast.error("Failed to load courses");
    } finally {
      setLoadingCourses(false);
    }
  };

  const loadUnits = async (courseId) => {
    setLoadingUnits(true);
    setUnits([]);
    setLessons([]);
    setEditingLesson(null);

    try {
      const data = await getUnitsByCourse(courseId);
      setUnits(data);
    } catch {
      toast.error("Failed to load units");
    } finally {
      setLoadingUnits(false);
    }
  };

  const loadLessons = async (unitId) => {
    if (!unitId) return;
    try {
      const data = await getLessonsByUnit(unitId);
      setLessons(data);
    } catch {
      toast.error("Failed to load lessons");
    }
  };

  /* ---------------- SAVE / EDIT ---------------- */

  const handleSaveLesson = async () => {
    if (!formData.unit_id || !formData.lesson_name || !formData.code) {
      toast.warning("Please fill all required fields");
      return;
    }

    const payload = {
      name: formData.lesson_name,
      code: formData.code,
      unit_id: parseInt(formData.unit_id, 10),
      notes: formData.notes,
    };

    setSaving(true);
    try {
      if (editingLesson) {
        await updateLesson(editingLesson.id, payload);
        toast.success("Lesson updated successfully!");
        setEditingLesson(null);
      } else {
        await createLesson(payload);
        toast.success("Lesson created successfully!");
      }

      setFormData((prev) => ({
        ...prev,
        lesson_name: "",
        code: "",
        notes: "",
      }));

      loadLessons(payload.unit_id);
    } catch {
      toast.error("Failed to save lesson");
    } finally {
      setSaving(false);
    }
  };

  const handleEditLesson = async (lesson) => {
    try {
      const full = await getLessonById(lesson.id);
      setEditingLesson(full);
      setFormData((prev) => ({
        ...prev,
        lesson_name: full.name || "",
        code: full.code || "",
        notes: full.notes || "",
        unit_id: full.unit_id?.[0] || prev.unit_id,
      }));
    } catch {
      toast.error("Failed to load lesson");
    }
  };

  /* ---------------- UI ---------------- */

  return (
    <div className="add-lesson-container">
      <div className="section-card">
        <h2>📚 Lesson Management</h2>

        <div className="form-grid">
          <div className="row-group">
            <select
              value={formData.department_id}
              onChange={(e) => {
                const id = e.target.value;
                setFormData({
                  department_id: id,
                  program_id: "",
                  semester_id: "",
                  course_id: "",
                  unit_id: "",
                  lesson_name: "",
                  code: "",
                  notes: "",
                });
                loadPrograms(id);
              }}
            >
              <option value="">Select Department</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>

            <select
              value={formData.program_id}
              disabled={loadingPrograms || !programs.length}
              onChange={(e) => {
                const id = e.target.value;
                setFormData((p) => ({ ...p, program_id: id, semester_id: "", course_id: "", unit_id: "" }));
                loadSemesters(id);
              }}
            >
              {loadingPrograms ? (
                <option className="dropdown-loader">⏳ Loading programs...</option>
              ) : (
                <>
                  <option value="">Select Program</option>
                  {programs.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </>
              )}
            </select>
          </div>

          <div className="row-group">
            <select
              value={formData.semester_id}
              disabled={loadingSemesters || !semesters.length}
              onChange={(e) => {
                const id = e.target.value;
                setFormData((p) => ({ ...p, semester_id: id, course_id: "", unit_id: "" }));
                loadCourses(id);
              }}
            >
              {loadingSemesters ? (
                <option className="dropdown-loader">⏳ Loading semesters...</option>
              ) : (
                <>
                  <option value="">Select Semester</option>
                  {semesters.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </>
              )}
            </select>

            <select
              value={formData.course_id}
              disabled={loadingCourses || !courses.length}
              onChange={(e) => {
                const id = e.target.value;
                setFormData((p) => ({ ...p, course_id: id, unit_id: "" }));
                loadUnits(id);
              }}
            >
              {loadingCourses ? (
                <option className="dropdown-loader">⏳ Loading courses...</option>
              ) : (
                <>
                  <option value="">Select Course</option>
                  {courses.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </>
              )}
            </select>

            <select
              value={formData.unit_id}
              disabled={loadingUnits || !units.length}
              onChange={(e) => {
                const id = e.target.value;
                setFormData((p) => ({ ...p, unit_id: id }));
                loadLessons(id);
              }}
            >
              {loadingUnits ? (
                <option className="dropdown-loader">⏳ Loading units...</option>
              ) : (
                <>
                  <option value="">Select Unit</option>
                  {units.map((u) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </>
              )}
            </select>
          </div>
        </div>
      </div>

      <div className="section-card">
        <h3>{editingLesson ? "✏️ Edit Lesson" : "➕ Create Lesson"}</h3>

        <div className="lesson-form">
          <input
            type="text"
            placeholder="Lesson Title"
            value={formData.lesson_name}
            onChange={(e) => setFormData((p) => ({ ...p, lesson_name: e.target.value }))}
          />
          <input
            type="text"
            placeholder="Lesson Code"
            value={formData.code}
            onChange={(e) => setFormData((p) => ({ ...p, code: e.target.value }))}
          />
        </div>

        <ReactQuill
          theme="snow"
          value={formData.notes}
          onChange={(v) => setFormData((p) => ({ ...p, notes: v }))}
          modules={quillModules}
          formats={quillFormats}
        />

        <div className="lesson-actions">
          <button
            className="animated-save-btn"
            onClick={handleSaveLesson}
            disabled={saving}
          >
            {saving ? <span className="loader" /> : editingLesson ? "🔄 Update Lesson" : "💾 Save Lesson"}
          </button>
        </div>
      </div>

      {/* ✅ SEARCH + TABLE */}
      {lessons.length > 0 && (
        <div className="section-card">
          <div className="unit-header">
            <h3>📋 Lessons ({lessons.length})</h3>
            <input
              type="text"
              placeholder="🔍 Search Lesson..."
              className="search-input"
              onChange={(e) => setSearch(e.target.value.toLowerCase())}
            />
          </div>

          <table className="programs-table">
            <tbody>
              {lessons
                .filter((l) => l.name.toLowerCase().includes(search))
                .map((lesson, i) => (
                  <tr key={lesson.id}>
                    <td>{i + 1}</td>
                    <td>{lesson.name}</td>
                    <td>{lesson.code}</td>
                    <td>
                      <button className="edit-btn" onClick={() => handleEditLesson(lesson)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      <ToastContainer position="bottom-right" autoClose={2000} />
    </div>
  );
};

export default AddLesson;

